import React, { useState, useEffect, useCallback } from 'react';
import {
  Outlet, useLocation, useParams,
} from '@tanstack/react-router';
import { RouterService, buildPaginatedPath } from '@piwikpro/router-crate';
import { ListPreference } from '@piwikpro/user-preferences';
import { TFunction } from 'react-i18next';
import {
  Pagination,
  TextField,
  Stack,
  StyledContainer,
  Layout,
  List,
  HelpLink,
  Information,
} from '@piwikpro/ui-components';
import debounce from 'lodash/debounce';
import {
  ConfigService,
  Pagination as PaginationManager,
  errorBoundary,
  withInject,
  InternalListLink,
  PermissionSelect,
} from '@piwikpro/platform';
import { AccessControlService } from '@piwikpro/access-control-crate/AccessControl';
import { PermissionLevel } from '@piwikpro/access-control-crate/userResourcePermissions';
import { permissionSelectOptions } from '../../../permissionOptions';

interface UserGroupPermissionsProps {
  userGroupId: string
  userPreferences: any
  routerService: RouterService
  config: ConfigService
  accessControl: AccessControlService
  appPermissionLevels: PermissionLevel[]
  Title: typeof React.Component
  total: number
  failed: boolean
  isLoading: boolean
  permissions: string[]
  t: TFunction
}

const UserGroupPermissions: React.FC<UserGroupPermissionsProps> = ({
  userGroupId,
  userPreferences,
  routerService,
  config,
  accessControl,
  appPermissionLevels,
  Title,
  total,
  failed,
  isLoading,
  permissions,
  t,
}) => {
  const params = useParams({
    from: '/_secured/administration/user-groups/$groupId/permissions/$page/$limit',
  });
  const { pathname } = useLocation();
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState<null |(() => void)>(null);
  const listPreference = new ListPreference(userPreferences.load(), '');

  const fetchUserGroupPermissions = async () => {
    try {
      const pagination = PaginationManager.fromQueryParams([], 0, params);
      await accessControl.fetchUserGroupPermissions(userGroupId, {
        search,
        limit: pagination.getLimit(),
        offset: pagination.offset,
      });
    // eslint-disable-next-line no-empty
    } catch {}
  };

  useEffect(() => {
    fetchUserGroupPermissions();
  }, []);

  const handleLimitChange = (limit: number) => {
    accessControl.fetchUserGroupPermissions(userGroupId, {
      search,
      limit,
      offset: 0,
    });

    listPreference.limit = limit;
    userPreferences.persist(listPreference);

    routerService.push(buildPaginatedPath(pathname, 1, limit));
  };

  const handlePageChange = (offset: number, pagination: any) => {
    accessControl.fetchUserGroupPermissions(userGroupId, {
      search,
      limit: pagination.getLimit(),
      offset,
    });

    routerService.push(buildPaginatedPath(
      pathname,
      pagination.getPageFromOffset(offset),
      pagination.getLimit(),
    ));
  };

  const handleSearchChange = useCallback(
    (event) => {
      if (searchDebounce) {
        (searchDebounce as any).cancel();
      }

      const searchValue = event.target.value as string;

      const handleSearch = debounce(() => {
        const pagination = PaginationManager.fromQueryParams([], 0, params);

        accessControl.fetchUserGroupPermissions(userGroupId, {
          search: searchValue,
          offset: pagination.offset,
          limit: pagination.getLimit(),
        });

        routerService.push(buildPaginatedPath(pathname, 1, pagination.getLimit()));
      }, config.get('LIST_SEARCH_DELAY'));

      setSearchDebounce(() => handleSearch);
      setSearch(searchValue);

      handleSearch();
    },
    [accessControl, config, routerService, pathname, params, searchDebounce, userGroupId],
  );

  const handlePermissionUpdate = async (values: any, api: any, entityId: string) => {
    try {
      await accessControl.updateUserGroupPermissions(entityId, userGroupId, { ...values });
    } catch (err) {
      api.resetForm();
    }

    api.setSubmitting(false);
  };

  const renderPermissionsRow = (permission: any) => {
    const userAccess = permission.attributes.access;

    return (
      <List.Item key={permission.id}>
        <InternalListLink
          to="/administration/apps/$appId"
          params={{ appId: permission.id }}
          label={permission.attributes.app_name}
        />
        <PermissionSelect
          initialValue={userAccess}
          options={permissionSelectOptions(appPermissionLevels, t, 'app')}
          onSubmit={(values, api) => handlePermissionUpdate(values, api, permission.id)}
        />
      </List.Item>
    );
  };

  const pagination = PaginationManager.fromQueryParams(permissions, total, params);

  return (
    <Title part={t('user-groups.details.permissions.title')}>
      <Layout>
        <Stack vertical spacing="wide">
          <Stack.Item fill>
            <Stack vertical horizontalAlignment="middle">
              <Stack.Item fill>
                <Information type="info">{t('user-groups.details.permissions.info')}</Information>
              </Stack.Item>
              <Stack.Item fill>
                {!failed && (
                  <StyledContainer border={['left', 'right', 'top']} padding="narrow" backgroundColor="gray">
                    <TextField
                      value={search}
                      name="search"
                      icon="search"
                      onChange={handleSearchChange}
                      placeholder={t('user-groups.details.permissions.search.placeholder')}
                      clearable
                    />
                  </StyledContainer>
                )}
                <List
                  columns={[
                    {
                      size: 5,
                      align: 'left',
                    },
                    {
                      size: 2,
                      align: 'right',
                    },
                  ]}
                  isLoading={isLoading}
                  error={failed}
                >
                  {!failed && permissions?.map(
                    groupWithPermissions => renderPermissionsRow(groupWithPermissions),
                  )}
                </List>
              </Stack.Item>
              {!failed && permissions?.length > 0 && (
                <Stack.Item fill>
                  <Pagination
                    onLimitChange={handleLimitChange}
                    limit={pagination.getLimit()}
                    total={total}
                    limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
                    startIndex={pagination.offset}
                    endIndex={pagination.offset + pagination.getLimit()}
                    onPageChange={e => handlePageChange(e, pagination)}
                  />
                </Stack.Item>
              )}
              <Stack.Item>
                <HelpLink href="https://help.piwik.pro/support/getting-started/websites-permissions/">
                  Permissions for a site or an app
                </HelpLink>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        </Stack>
      </Layout>
      <Outlet />
    </Title>
  );
};

export default withInject({
  config: 'config',
  appPermissionLevels: 'appPermissionLevels',
  userPreferences: 'userPreferences',
  Title: 'TitleCrate.components.Title',
  accessControl: 'AccessControlCrate.accessControl',
  routerService: 'RouterCrate.router',
})(errorBoundary(UserGroupPermissions));
